import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import CompetencyTree from 'builder/Knowledge/components/TreeView/CompetencyTree';
import compareDocumentItems from 'builder/Knowledge/components/TreeView/compareDocumentItems';
import { 
  createKnowledgeMapItem, 
  createKnowledgeMapItemAssociation, 
  deleteAllKnowledgeMapItemsService,
} from 'Workspace/Knowledge/services';
import { 
  KnowledgeMapFilenamePrefix,
  KnowledgeMapColumnHeaders,
  KnowledgeMapErrorMessageKeys,
  regexFindDoubleQuotes,
  competencyTitleKeyPrefixDelimitor,
  escapeDoubleQuotation,
  errorMessageKeys,
  regexFindDot,
  doubleQuotes,
} from 'constants/knowledgeMap';
import knowledgePagemessages from 'Workspace/Knowledge/components/KnowledgePage/messages';
import { 
  competencyDocumentItemCreateMutation,
  competencyDocumentItemAssociationMutation,
  competencyDocumentItemDeleteMutation,
} from 'Workspace/Knowledge/utils/graphqlMutation';
import importMessages from '../components/KnowledgeMapImportModal/messages';
import { 
  MAX_KNOWLEDGE_MAP_SHORT_CODE,
  KNOWLEDGE_MAP_PARENT_KEY,
  KNOWLEDGE_MAP_CHILD_KEY,
  MAX_DESCRIPTION,
  MAX_ENABLING_OBJECTIVE_TITLE,
  MAX_LEARNING_OBJECTIVE_TITLE,
} from '../components/KnowledgeMapImportModal/constants';

const {
  KEY, 
  VALUE, 
  DUPLICATETITLE, 
  SHORTCODEVALUE,
  DESCRIPTIONCODEVALUE,
  LONGSHORTCODE,
  LONGDESCRIPTION, 
  DUPLICATESHORTCODE, 
  MISSINGTITLE,
  INVALIDPARENT,
  PARENTMISSINGCHILDTITLE,
  LONGENABLINGOBJECTIVETITLE,
  LONGLEARNINGOBJECTIVETITLE,
  CHILDMISSINGPARENTTITLE,
  ISLEARNINGOBJECTIVEPRESENT,
  INVALIDLOEORELATION,
} = KnowledgeMapErrorMessageKeys;

const {
  ID,
  LEARNING_OBJECTIVE_TITLE,
  ENABLING_OBJECTIVE_TITLE,
  EO_LO_RELATION,
  DESCRIPTION, 
  SHORTCODE,
} = KnowledgeMapColumnHeaders;

const CSV_HEADER_ROW_NUMBER = '1';

const EMPTY_STRING = '';
const KEY_INDEX_ZERO = 0;
const KEY_INDEX_ONE = 1;

const messages = { ...knowledgePagemessages, ...importMessages };

/**
 * get column headers from knowledgeMap worksheet and return array
 * 
 * @param {Object} knowledgeMapWorksheet.
 * @return {Array} - column header list.
 */
export const getColumnHeaders = (knowledgeMapWorksheet) => {
  const resultArray = Object.keys(knowledgeMapWorksheet).reduce(( columnHeaderList, key ) => {
    const { 1: rowNumber } = getCSVHeaderAddressKey(key);
    const { v: value } = knowledgeMapWorksheet[key];
    
    if (rowNumber === CSV_HEADER_ROW_NUMBER) {
      columnHeaderList.push(value);
    }
    return columnHeaderList;
  }, []);

  return resultArray;
};

/**
 * validate knowledgeMap csv column headers and return boolean
 * 
 * @param {Object} knowledgeMapColumnObject 
 * @param {Array} knowledgeMapColumnArray
 * @return {Boolean} - boolean value stating column headers are valid (or) not.
 */
export const validateColumnHeaders = (knowledgeMapColumnObject, knowledgeMapColumnArray) => {
  for(let key in knowledgeMapColumnObject){
    const knowledgeMapHeaderExists = knowledgeMapColumnArray.some( 
      (knowledgeMapColumnHeader) => knowledgeMapColumnHeader.toLowerCase().includes(
        knowledgeMapColumnObject[key].toLowerCase()
      )
    );
    
    if(!knowledgeMapHeaderExists) {
      return false;
    }
  }
  return true;
};

/**
 * get column headers key from knowledgeMap worksheet and return object
 * 
 * @param {Object} knowledgeMapWorksheet
 * @return {Object} - object containing header address keys for all column headers.
 */
export const getColumnHeaderKey = (knowledgeMapWorksheet) => {
  let headerAddressKey = {};
  for(let key in knowledgeMapWorksheet){
    const { 0: addressKey } = getCSVHeaderAddressKey(key);
    const { v: value } = knowledgeMapWorksheet[key];
    if (!value) {
      continue;
    }
      
    if (value.toString().includes(ENABLING_OBJECTIVE_TITLE) && !headerAddressKey[ENABLING_OBJECTIVE_TITLE]) {
      headerAddressKey[ENABLING_OBJECTIVE_TITLE] = addressKey;
    }

    if (value.toString().includes(LEARNING_OBJECTIVE_TITLE) && !headerAddressKey[LEARNING_OBJECTIVE_TITLE]) {
      headerAddressKey[LEARNING_OBJECTIVE_TITLE] = addressKey;
    }
      
    if (value.toString().includes(EO_LO_RELATION) && !headerAddressKey[EO_LO_RELATION]) {
      headerAddressKey[EO_LO_RELATION] = addressKey;
    } 
      
    if (value.toString().includes(SHORTCODE) && !headerAddressKey[SHORTCODE]) {
      headerAddressKey[SHORTCODE] = addressKey;
    } 
      
    if (value.toString().includes(DESCRIPTION) && !headerAddressKey[DESCRIPTION]) {
      headerAddressKey[DESCRIPTION] = addressKey;
    }

    if (headerAddressKey[ENABLING_OBJECTIVE_TITLE] && 
      headerAddressKey[LEARNING_OBJECTIVE_TITLE] && 
      headerAddressKey[EO_LO_RELATION] && 
      headerAddressKey[DESCRIPTION] && 
      headerAddressKey[SHORTCODE]) {
      break;
    }
  }
  return headerAddressKey;
};

/**
 * split the alphabet, numbers and return array
 * 
 * @param {string} key
 * @return {Array} - array containing column and row value for a key.
 */
export const getCSVHeaderAddressKey = (key) => key.match(/[a-zA-Z]+|[0-9]+/g);

/**
 * get all title headers values from knowledgeMap worksheet and return array
 * 
 * @param {Object} knowledgeMapWorksheet
 * @param {string} headerAddressKey
 * @return {Array} - array containing all titles in the worksheet.
 */
export const getAllTitleFields = (knowledgeMapWorksheet, headerAddressKey) => {
  const allTitleArray = Object.keys(knowledgeMapWorksheet).reduce((allTitleList, key) => {
    const { v: value } = knowledgeMapWorksheet[key];
    const { 1: rowNumberValue } = getCSVHeaderAddressKey(key);

    if ((key.includes(headerAddressKey[ENABLING_OBJECTIVE_TITLE]) || 
      key.includes(headerAddressKey[LEARNING_OBJECTIVE_TITLE])) && 
      rowNumberValue !== CSV_HEADER_ROW_NUMBER) {
      allTitleList.push({
        [KEY]: key, 
        [VALUE]: value,
      });
    } 
    return allTitleList;
  }, []);

  return allTitleArray;
};

/**
 * get knowledge map array of object for upload worksheet
 * 
 * @param {Object} knowledgeMapWorksheet
 * @returns {Object} knowledgeMapItems
 */
export const getKnowledgeMapWorksheetArray = (knowledgeMapWorkSheet) => {
  const knowledgeMapItems = Object.keys(knowledgeMapWorkSheet).map((key) => {
    const knowledgeMapItem = knowledgeMapWorkSheet[key];
    return getKnowledgeMapObject(knowledgeMapItem);
  });
  return knowledgeMapItems;
};

/**
 * get knowledge map object by column header
 * 
 * @param {Object} knowledgeMapItem
 * @returns {Object} knowledgeMapResultObject
 */
export const getKnowledgeMapObject = (knowledgeMapItem) => {
  const knowledgeMapResultObject = Object.keys(knowledgeMapItem).reduce((knowledgeMapObject, key)=>{
    if (key.includes(ENABLING_OBJECTIVE_TITLE)) {
      knowledgeMapObject[ENABLING_OBJECTIVE_TITLE] = knowledgeMapItem[key];
    }
    if (key.includes(LEARNING_OBJECTIVE_TITLE)) {
      knowledgeMapObject[LEARNING_OBJECTIVE_TITLE] = knowledgeMapItem[key];
    }
    if (key.includes(DESCRIPTION)) {
      knowledgeMapObject[DESCRIPTION] = knowledgeMapItem[key];
    }
    if (key.includes(EO_LO_RELATION)) {
      knowledgeMapObject[EO_LO_RELATION] = knowledgeMapItem[key];
    }
    if (key.includes(SHORTCODE)) {
      knowledgeMapObject[SHORTCODE] = knowledgeMapItem[key];
    }
    return knowledgeMapObject;
  }, {});

  return knowledgeMapResultObject;
};

/**
 * build array of objects with duplicate title error type and return array of objects
 * 
 * @param {object} headerAddressKey
 * @param {string} key
 * @param {string} value
 * @param {string} titleRowNumberValue
 * @param {array} knowledgeMapFieldsArray
 * @return {String} - title row number.
 */
export const buildDuplicateTitleArray = (
  headerAddressKey, 
  key, 
  value, 
  titleRowNumberValue, 
  knowledgeMapFieldsArray,
  knowledgeMapWorksheet
) => {
  if(key.includes(headerAddressKey[ENABLING_OBJECTIVE_TITLE]) || 
    key.includes(headerAddressKey[LEARNING_OBJECTIVE_TITLE])) {
    titleRowNumberValue = getCSVHeaderAddressKey(key) && getCSVHeaderAddressKey(key)[1];
    const isLearningObjectiveTitle = key.includes(headerAddressKey[LEARNING_OBJECTIVE_TITLE]);
    knowledgeMapFieldsArray.push({
      [KEY]: key, 
      [VALUE]: value,
      [LONGENABLINGOBJECTIVETITLE]: !isLearningObjectiveTitle && value.length > MAX_ENABLING_OBJECTIVE_TITLE,
      [LONGLEARNINGOBJECTIVETITLE]: isLearningObjectiveTitle && value.length > MAX_LEARNING_OBJECTIVE_TITLE,
      [DUPLICATETITLE]: isDuplicateValue(knowledgeMapFieldsArray, VALUE, value),
      [INVALIDLOEORELATION]: isLearningObjectiveTitle && 
        getWorkSheetCellValue(knowledgeMapWorksheet, headerAddressKey, titleRowNumberValue, EO_LO_RELATION),
    });
  }
  return titleRowNumberValue;
};

/**
 * check whether Duplicate value exists
 *
 * @param {array} KnowledgeMapFieldsList
 * @param {string} knowledgeMapField
 * @param {string} value
 * @return {Boolean} - boolean value stating whether field exists in the field list.
 */
export const isDuplicateValue = (KnowledgeMapFieldsList, knowledgeMapField, value) => 
  (KnowledgeMapFieldsList.some((val) => val[knowledgeMapField] === value));

/**
 * check whether Parent has no child title
 *
 * @param {object} knowledgeMapWorksheet
 * @param {object} headerAddressKey
 * @param {string} parentRowNumberValue
 * @return {Object || undefined} - If parent has child or child has parent title then it will return object else return undefined
 */
export const getWorkSheetCellValue = (
  knowledgeMapWorksheet, 
  headerAddressKey, 
  parentRowNumberValue, 
  columnName
) => (knowledgeMapWorksheet[`${headerAddressKey[columnName]}${parentRowNumberValue}`]);


/**
 * build array of objects with short code error types and return array of objects
 * 
 * @param {object} headerAddressKey
 * @param {string} key
 * @param {string} value
 * @param {string} titleRowNumberValue
 * @param {array} knowledgeMapFieldsArray
 * 
 */
export const buildShortCodeErrorArray = (
  headerAddressKey, 
  key, 
  value, 
  titleRowNumberValue,  
  knowledgeMapFieldsArray
) => {
  if (key.includes(headerAddressKey[SHORTCODE])) {
    const { 1: shortCodeRowNumberValue } = getCSVHeaderAddressKey(key);
    const invalidTitleRow = titleRowNumberValue !== shortCodeRowNumberValue;
    knowledgeMapFieldsArray.push({ 
      [KEY]:  invalidTitleRow ? `${headerAddressKey[ENABLING_OBJECTIVE_TITLE]}${shortCodeRowNumberValue}` : key, 
      [SHORTCODEVALUE]: value, 
      [LONGSHORTCODE]: value.length > MAX_KNOWLEDGE_MAP_SHORT_CODE,
      [DUPLICATESHORTCODE]: isDuplicateValue(knowledgeMapFieldsArray, [SHORTCODEVALUE], value),
      [MISSINGTITLE]: invalidTitleRow ? true : false});
  }
};

/**
 * build array of objects with invalid parent title error type and return array of objects
 * 
 * @param {object} headerAddressKey
 * @param {string} key
 * @param {string} value
 * @param {array} knowledgeMapFieldsArray
 * @param {array} allTitleArray
 * @param {object} knowledgeMapWorksheet
 */
export const buildInvalidParentArray = (
  headerAddressKey,  
  key, 
  value, 
  knowledgeMapFieldsArray, 
  allTitleArray, 
  knowledgeMapWorksheet
) => {
  if (key.includes(headerAddressKey[EO_LO_RELATION])) {
    const { 1: parentRowNumberValue } = getCSVHeaderAddressKey(key);
    knowledgeMapFieldsArray.push({
      [KEY]: key, 
      [INVALIDPARENT]: !isDuplicateValue(allTitleArray, VALUE, value),
      [PARENTMISSINGCHILDTITLE]: !getWorkSheetCellValue(
        knowledgeMapWorksheet, 
        headerAddressKey, 
        parentRowNumberValue, 
        ENABLING_OBJECTIVE_TITLE
      ),
    });
  }
  if (key.includes(headerAddressKey[ENABLING_OBJECTIVE_TITLE])) {
    const { 1: enablingObjectiveRowNumberValue } = getCSVHeaderAddressKey(key);
    knowledgeMapFieldsArray.push({
      [KEY]: key, 
      [ISLEARNINGOBJECTIVEPRESENT]: getWorkSheetCellValue(
        knowledgeMapWorksheet, 
        headerAddressKey, 
        enablingObjectiveRowNumberValue, 
        LEARNING_OBJECTIVE_TITLE
      ),
      [CHILDMISSINGPARENTTITLE]: !getWorkSheetCellValue(
        knowledgeMapWorksheet, 
        headerAddressKey, 
        enablingObjectiveRowNumberValue, 
        EO_LO_RELATION
      ),
    });
  }
};

/**
 * build array of objects with missing title error type and return array of objects
 * 
 * @param {object} headerAddressKey
 * @param {string} key
 * @param {string} value
 * @param {string} titleRowNumberValue
 * @param {array} knowledgeMapFieldsArray
 */
export const buildMissingTitleArray = (
  headerAddressKey, 
  key, 
  value, 
  titleRowNumberValue, 
  knowledgeMapFieldsArray
) => {
  if (key.includes(headerAddressKey[DESCRIPTION])) {
    const { 1: descriptionRowNumberValue }=  getCSVHeaderAddressKey(key);
    const missingTitle = titleRowNumberValue !== descriptionRowNumberValue;
    knowledgeMapFieldsArray.push({ 
      [KEY]: missingTitle ? `${headerAddressKey[ENABLING_OBJECTIVE_TITLE]}${descriptionRowNumberValue}` : key, 
      [MISSINGTITLE]: missingTitle,
      [DESCRIPTIONCODEVALUE]: value, 
      [LONGDESCRIPTION]: value.length > MAX_DESCRIPTION,
    });
  }
};

/**
 * validate worksheet and build array of objects with various error types and return array of objects
 * 
 * @param {Object} knowledgeMapWorksheet
 * @return {Array} - list of knowledge map fields.
 */
export const validateKnowledgeMapCSVFileData = (knowledgeMapWorksheet) => {
  let titleRowNumberValue;
  const headerAddressKey =  getColumnHeaderKey(knowledgeMapWorksheet);    
  const allTitleArray = getAllTitleFields(knowledgeMapWorksheet, headerAddressKey);
  
  const knowledgeMapFieldsArray = Object.keys(knowledgeMapWorksheet).reduce((knowledgeMapFieldsList, key) => {
    const { v: value } = knowledgeMapWorksheet[key];
    const { 1: rowNumberValue } = getCSVHeaderAddressKey(key);
    
    if(rowNumberValue === CSV_HEADER_ROW_NUMBER) {
      return knowledgeMapFieldsList;
    }

    titleRowNumberValue = buildDuplicateTitleArray(
      headerAddressKey, 
      key, 
      value, 
      titleRowNumberValue, 
      knowledgeMapFieldsList,
      knowledgeMapWorksheet,
    );
    buildShortCodeErrorArray(headerAddressKey, key, value, titleRowNumberValue, knowledgeMapFieldsList);
    buildInvalidParentArray(headerAddressKey,  key, value, knowledgeMapFieldsList, allTitleArray, knowledgeMapWorksheet);
    buildMissingTitleArray(headerAddressKey, key, value, titleRowNumberValue, knowledgeMapFieldsList);
    return knowledgeMapFieldsList;
  }, []);
  return knowledgeMapFieldsArray;
};


/**
 * build error message string
 * 
 * @param {string} Key
 * @param {string} errorMessage
 * @return {String} - error message with row and column value appended.
 */
export const buildErrorMessage = (Key, errorMessage) => {
  const [ headerAddressKeyColumn, headerAddressKeyRow ] = getCSVHeaderAddressKey(Key);
  const errorMessageRow  = `${intl.formatMessage(messages.row)} ${headerAddressKeyRow}`;
  const errorMessageColumn = `${intl.formatMessage(messages.column)} ${headerAddressKeyColumn}`;
  return `${errorMessageRow} / ${errorMessageColumn} : ${errorMessage}`;
};

/**
 * get knowledge map csv data errormessage array
 * 
 * @param {Array} knowledgeMapCSVObjectArray
 * @return {Array} - list of knowledge map errors.
 */
export const getKnowledgeMapCSVErrorMessage = (knowledgeMapCSVObjectArray, isColumnHeaders) => {
  const errorMessageArray = [];
  let errorMessage = null;

  if (isColumnHeaders && knowledgeMapCSVObjectArray.length > 0) {
    knowledgeMapCSVObjectArray.forEach((errorMessageObject) => {
      Object.keys(errorMessageObject).forEach((errorObjectKey) => {
        const errorMessageKey = errorMessageKeys[errorObjectKey];
        if(errorMessageKey && errorMessageObject[errorObjectKey]) {
          errorMessage = intl.formatMessage(messages[errorMessageKey]);
          errorMessageArray.push(buildErrorMessage(errorMessageObject.Key, errorMessage));
        }
      });
    });
  } else if (!isColumnHeaders) {
    errorMessage = intl.formatMessage(messages.knowledgeMapMissingDataHeader);
    errorMessageArray.push(errorMessage);
  } else {
    errorMessage = intl.formatMessage(messages.knowledgeMapEmptySpreadsheet);
    errorMessageArray.push(errorMessage);
  }
  return errorMessageArray;
};


/**
 * appends competency items to competency List
 * 
 * @param {object} item
 * @param {array} competencyList
 * @param {string} parentTitle
 * 
 */
const appendCompetencyList = (item , competencyList, parentTitle) => {
  const { 
    id,
    abbreviatedStatement, 
    fullStatement, 
    humanCodingScheme,
  } = item;

  const updatedAbbreviatedStatement = abbreviatedStatement?.replace(regexFindDoubleQuotes, doubleQuotes);

  competencyList.push({
    [ID]: id,
    [EO_LO_RELATION]: parentTitle?.replace(regexFindDoubleQuotes, doubleQuotes),
    [ENABLING_OBJECTIVE_TITLE]: parentTitle ? updatedAbbreviatedStatement : '',
    [LEARNING_OBJECTIVE_TITLE]: !parentTitle ? updatedAbbreviatedStatement : '',
    [DESCRIPTION]: fullStatement?.replace(regexFindDoubleQuotes, doubleQuotes),
    [SHORTCODE]: humanCodingScheme?.replace(regexFindDoubleQuotes, doubleQuotes),
  });
};

/**
 * get knowledge map data and construct competency list for excel download
 * 
 * @param {object} knowledgeContent
 * @return {Array} - list of knowledge map competency list.
 */
export const getCompetencyList = (knowledgeContent) => {
  const competencyTree = new CompetencyTree(knowledgeContent);
  const sortedCompetencyRootItems = competencyTree.getRootItems().sort(compareDocumentItems);
  
  const competencyList = sortedCompetencyRootItems.reduce((competencyListArray, item) => {
    const {id: parentId,
      abbreviatedStatement: parentTitle} = item;

    appendCompetencyList(item, competencyListArray);
    const childItems = competencyTree.allChildrenByParentId[parentId];
    childItems.map( (childItemId) => {
      const childItem = competencyTree.itemsById[childItemId];
      appendCompetencyList(childItem, competencyListArray, parentTitle);
    });
    return competencyListArray;
  }, []);
  
  return competencyList;
};

/**
 * appends Knowledge map file name prefix and knowledge map title
 * 
 * @param {object} knowledgeContent
 * @return {String} - knowledge map title with knowledge map prefix appended.
 */
export const getKnowledgeMapFileName = (knowledgeContent) => (`${KnowledgeMapFilenamePrefix}${knowledgeContent.title}`);

//Knowledge map CSV header id and default names
export const CSVHeaderNames = Object.freeze([{
  id: ID,
  displayName: messages.id.defaultMessage,
},{
  id: LEARNING_OBJECTIVE_TITLE,
  displayName: messages.learningObjectiveTitle.defaultMessage,
},{
  id: ENABLING_OBJECTIVE_TITLE,
  displayName: messages.enablingObjectiveTitle.defaultMessage,
},{
  id: EO_LO_RELATION,
  displayName: messages.EOLORelation.defaultMessage,
},{
  id: DESCRIPTION,
  displayName: messages.description.defaultMessage,
},{
  id: SHORTCODE,
  displayName: messages.shortCode.defaultMessage,
},
]);

/**
 * add duplicated knowledge map items
 * 
 * @param {Array} competencyItems
 * @param {Object} competencyDocument
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} response of createKnowledgeMapItem
 */
export const addCompetencyItems = async (competencyItems, competencyDocument, apolloClient) => {
  const { id: competencyDocumentId } = competencyDocument;

  const knowledgeMapItemsInsertQuery = Object.keys(competencyItems).reduce( (knowledgeMapItemInsertQuery, knowledgeMapKey) => {
    let {abbreviatedStatement, fullStatement, humanCodingScheme, titleKey} = competencyItems[knowledgeMapKey];
    
    abbreviatedStatement = abbreviatedStatement.replace(regexFindDoubleQuotes, escapeDoubleQuotation);
    fullStatement = fullStatement.replace(regexFindDoubleQuotes, escapeDoubleQuotation);
    humanCodingScheme = humanCodingScheme.replace(regexFindDoubleQuotes, escapeDoubleQuotation);
    titleKey = titleKey.replace(regexFindDot, competencyTitleKeyPrefixDelimitor);
    
    knowledgeMapItemInsertQuery += competencyDocumentItemCreateMutation(
      titleKey, 
      competencyDocumentId,
      abbreviatedStatement, 
      fullStatement, 
      humanCodingScheme,
    );

    return knowledgeMapItemInsertQuery;
  }, '');

  return await createKnowledgeMapItem(knowledgeMapItemsInsertQuery, apolloClient, competencyDocumentId);
};

/**
 * replaceQuoteWithEscape is a utility to replace " with \" to avoid invalid string error.
 * 
 * @param {string} 
 * @returns {string} 
 */
export const replaceQuoteWithEscape = (statement) => statement.replace(regexFindDoubleQuotes, escapeDoubleQuotation);

/**
 * replaceDotWithDelimitor is a utility to replace . with DOT if KM got rearranged and ItemAlias is in decimal.
 * 
 * @param {string} 
 * @returns {string} 
 */
export const replaceDotWithDelimitor = (statement) => statement.replace(regexFindDot, competencyTitleKeyPrefixDelimitor);

/**
 * insert knowledge map items and get the response with the knowledge map item ids
 * 
 * @param {Object} KnowledgeMapDetails
 * @param {string} knowledgeMapId
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} response of createKnowledgeMapItem
 */
export const insertKnowledgeMapItems = async (KnowledgeMapDetails, knowledgeMapId, apolloClient) => {
  const knowledgeMapParentArray = {};
  const knowledgeMapChildArray = [];
  let knowledgeMapItemAlias = EMPTY_STRING;
  const {
    SHORTCODE,
  } = KnowledgeMapColumnHeaders;
  const knowledgeMapItemsInsertQuery = Object.keys(KnowledgeMapDetails).reduce( (knowledgeMapItemInsertQuery, knowledgeMapKey) => {
    const {
      [ENABLING_OBJECTIVE_TITLE] : enabingObjectiveTitle = EMPTY_STRING, 
      [LEARNING_OBJECTIVE_TITLE]: learningObjectiveTitle = EMPTY_STRING,
      [DESCRIPTION]: description = EMPTY_STRING,
      [EO_LO_RELATION]: parent = EMPTY_STRING,
      [SHORTCODE]: shortCode = EMPTY_STRING,
    } = KnowledgeMapDetails[knowledgeMapKey];

    let { abbreviatedStatement, humanCodingScheme, fullStatement } =  KnowledgeMapDetails[knowledgeMapKey];
    let escapedDescription = '';
    let escapedShorCode = '';
    
    if (!abbreviatedStatement) {
      const escapedEnabingObjectiveTitle = enabingObjectiveTitle.replace(regexFindDoubleQuotes, escapeDoubleQuotation);
      const escapedLearningObjectiveTitle = learningObjectiveTitle.replace(regexFindDoubleQuotes, escapeDoubleQuotation);
      escapedDescription = description.replace(regexFindDoubleQuotes, escapeDoubleQuotation);
      escapedShorCode = shortCode.replace(regexFindDoubleQuotes, escapeDoubleQuotation);
      const escapedTitle = escapedLearningObjectiveTitle || escapedEnabingObjectiveTitle;
      const escapedParent = parent.replace(regexFindDoubleQuotes, escapeDoubleQuotation);

      if(!parent) {
        const knowledgeMapParentCurrentPlace = Object.keys(knowledgeMapParentArray).length + 1;
        knowledgeMapParentArray[escapedTitle] = `${ KNOWLEDGE_MAP_PARENT_KEY }${ knowledgeMapParentCurrentPlace }`;
        knowledgeMapItemAlias = `${knowledgeMapParentArray[escapedTitle]}`;
      } else {
        const parentKey = knowledgeMapParentArray[escapedParent];
        const numberOfChildInParent = knowledgeMapChildArray[parentKey];
        const numberOfChildInParentIncrementor = (numberOfChildInParent) ? numberOfChildInParent + 1: 1;
        knowledgeMapChildArray[parentKey] = numberOfChildInParentIncrementor;
        const currentChildKey = KNOWLEDGE_MAP_CHILD_KEY + (numberOfChildInParentIncrementor);
        knowledgeMapItemAlias = `${knowledgeMapParentArray[escapedParent]}_${currentChildKey}`;
      }
      abbreviatedStatement = `${ knowledgeMapItemAlias }-${ escapedTitle }`;
    } else {
      // for Duplicate KM.
      abbreviatedStatement = replaceQuoteWithEscape(abbreviatedStatement);
      knowledgeMapItemAlias = replaceDotWithDelimitor(abbreviatedStatement.split('-')[0]);
      escapedDescription = replaceQuoteWithEscape(fullStatement);
      escapedShorCode = replaceQuoteWithEscape(humanCodingScheme);
    }
    
    knowledgeMapItemInsertQuery += competencyDocumentItemCreateMutation(
      knowledgeMapItemAlias,
      knowledgeMapId,
      abbreviatedStatement,
      escapedDescription,
      escapedShorCode,
    );
    return knowledgeMapItemInsertQuery;
  }, '');
  return await createKnowledgeMapItem(knowledgeMapItemsInsertQuery, apolloClient, knowledgeMapId);
};

/**
 * delete all knowledge map items 
 * 
 * @param {Array} knowledgeMapItems
 * @param {string} knowledgeMapId
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} response of createKnowledgeMapItem
 */

export const deleteAllKnowledgeMapItems = async (knowledgeMapItems, knowledgeMapId, apolloClient) => {
  
  const knowledgeMapItemsDeleteQuery = Object.keys(knowledgeMapItems).reduce( (knowledgeMapItemDeleteQuery, knowledgeMapKey) => {
    const {
      titleKey : knowledgeMapItemAlias, 
      id,
    } = knowledgeMapItems[knowledgeMapKey];

    const escapedKnowledgeMapItemAlias = knowledgeMapItemAlias.replace(regexFindDot, competencyTitleKeyPrefixDelimitor);

    knowledgeMapItemDeleteQuery += competencyDocumentItemDeleteMutation(
      escapedKnowledgeMapItemAlias, 
      knowledgeMapId, 
      id
    );
    return knowledgeMapItemDeleteQuery;
  }, '');
  return await deleteAllKnowledgeMapItemsService(knowledgeMapItemsDeleteQuery, apolloClient, knowledgeMapId);
};

/**
 * insert knowledge map items association to map the parent child relationship
 * 
 * @param {Object} knowledgeMapItem
 * @param {string} knowledgeMapId
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} response of createKnowledgeMapItemAssociation
 */
export const insertKnowledgeMapAssociation = async (knowledgeMapItem, knowledgeMapId, apolloClient, parentItemId) => {
  const KnowledgeMapItemAssociationsInsertQuery = Object.keys(knowledgeMapItem.data).reduce( (KnowledgeMapItemAssociationInsertQuery, insertedKey) => {
    const parentChildRelation = insertedKey.split('_');
    const parentId = parentItemId || knowledgeMapItem.data[parentChildRelation[KEY_INDEX_ZERO]].documentItem.id;
    if(parentChildRelation[KEY_INDEX_ONE]){
      const childItemId = `${knowledgeMapItem.data[insertedKey].documentItem.id}`;

      KnowledgeMapItemAssociationInsertQuery += competencyDocumentItemAssociationMutation(
        insertedKey, 
        childItemId, 
        parentId, 
        knowledgeMapId
      );
    }
    return KnowledgeMapItemAssociationInsertQuery;
  },'');

  if(KnowledgeMapItemAssociationsInsertQuery){
    return await createKnowledgeMapItemAssociation(KnowledgeMapItemAssociationsInsertQuery, apolloClient, knowledgeMapId);
  }
  return null;
};